import React, {useEffect, useState} from 'react';

import {Banner, Icon, Text, Anchor} from '@albert-io/atomic';
import type {ClassroomModelV1} from '@albert-io/models';
import systemTimeOffsetStore from 'generic/SystemTimeOffset/SystemTimeOffset.store';
import {renderMoment} from 'lib/timeUtils';

import {isAutoArchivingSoon} from '../ClassDetail/AutoArchiveChip';
import {useTeacherClassrooms} from '../hooks/useTeacherClassrooms';

/** The yearly auto-archive runs July 1; the "recently archived" notice shows July 1–15. */
const getSeasonBounds = () => {
  const now = systemTimeOffsetStore.getCurrentTime();
  // July is month index 6.
  return {
    start: now.clone().month(6).date(1).startOf('day'),
    end: now.clone().month(6).date(15).endOf('day')
  };
};

const isAutoArchiveSeason = (): boolean => {
  const {start, end} = getSeasonBounds();
  // Inclusive bounds — default isBetween is exclusive and would drop July 1 at midnight.
  return systemTimeOffsetStore.getCurrentTime().isBetween(start, end, undefined, '[]');
};

/**
 * Whether a class was auto-archived in THIS season — its auto-archive date (always July 1, per mandark)
 * falls in the current window. Excludes classes archived in prior years.
 */
const wasAutoArchivedThisSeason = (classroom: ClassroomModelV1): boolean => {
  const date = classroom.getAutoArchiveDate?.();
  const {start, end} = getSeasonBounds();
  return Boolean(date) && start.isSameOrBefore(date) && end.isSameOrAfter(date);
};

const DETAIL_COPY =
  'Archived classes are unavailable to students, and prevents additional enrollments. You can still ' +
  'view data from this class, but you will not be able to create new assignments. Unarchive your ' +
  'class at any time in class settings.';

export const AutoArchiveBanner = () => {
  const {archivedClassrooms, classrooms, fetchArchivedClassrooms} = useTeacherClassrooms();
  const [expanded, setExpanded] = useState(false);

  const season = isAutoArchiveSeason();

  // The "were archived" notice is about classes that were just auto-archived — those live in the
  // archived list, which is otherwise only fetched lazily (via the sidemenu's "Show Archived"). Pull it
  // in during the season window so the notice reflects whether THIS teacher actually had classes archived.
  useEffect(() => {
    if (season && archivedClassrooms.isEmpty()) {
      fetchArchivedClassrooms();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [season]);

  const soonest = season
    ? null
    : classrooms
        .filter((classroom) => isAutoArchivingSoon(classroom))
        .minBy((classroom) => classroom.getAutoArchiveDate().valueOf());

  // During the season, only count classes auto-archived in THIS run — not ones archived in prior years.
  const hasArchivedThisSeason =
    season && archivedClassrooms.some((c) => wasAutoArchivedThisSeason(c));

  // Outside the season: show only when a class is auto-archiving soon (active list). During the season:
  // show only if the teacher had a class auto-archived this season. Active and archived are separate
  // lists, so the old `classrooms.isEmpty()` check was wrong on both ends.
  if ((!season && !soonest) || (season && !hasArchivedThisSeason)) {
    return null;
  }

  const title = soonest
    ? `Classes will be automatically archived on ${renderMoment(
        soonest.getAutoArchiveDate(),
        'dateFormalShortWithFullMonth'
      )}`
    : 'Classes were automatically archived';

  return (
    <Banner alignContent='center' type='info'>
      <div className='flex grow items-center gap-x2'>
        <Icon color='inherit' icon='box-archive' iconStyle='regular' />
        <div className='flex flex-col items-start'>
          <Text bold color='inherit'>
            {title}
          </Text>
          {expanded ? (
            <Text color='inherit'>{DETAIL_COPY}</Text>
          ) : (
            <Anchor
              noUnderline
              className='text-inherit'
              onClick={() => setExpanded(true)}
              as='button'
            >
              Show more info
            </Anchor>
          )}
        </div>
      </div>
    </Banner>
  );
};
